import type { AssistantSuggestion } from '../api/client';
import {
  useAssistantSuggestions,
  useDismissAssistantSuggestion,
  useRunAnalyst,
} from '../api/queries';
import { applyProposal } from '../chat/applyProposal';
import type { Budget } from '../budget/useBudget';
import { Button } from './ui';
import { useToast } from './ui/ToastProvider';

export function RuleInvestigation({ budget }: { budget: Budget }) {
  const toast = useToast();
  const { data } = useAssistantSuggestions();
  const run = useRunAnalyst();
  const dismiss = useDismissAssistantSuggestion();
  const suggestions = (data?.suggestions ?? []).filter(
    (row) => row.proposal?.kind === 'sorting_rule'
  );

  async function confirm(suggestion: AssistantSuggestion) {
    if (!suggestion.proposal) return;
    try {
      await applyProposal(suggestion.proposal, budget);
      toast.show(`Done: ${suggestion.proposal.summary}`);
      await dismiss.mutateAsync(suggestion.id);
    } catch (error) {
      toast.show(error instanceof Error ? error.message : 'Could not add that rule.');
    }
  }

  return (
    <div className="assistant-list">
      <div className="assistant-card__actions" style={{ marginTop: 0 }}>
        <p className="assistant-card__body" style={{ margin: 0 }}>
          {run.isPending
            ? 'Looking through filings…'
            : suggestions.length === 0
              ? 'Find merchants that should become rules.'
              : `${suggestions.length} suggested`}
        </p>
        <Button
          size="sm"
          emphasis="secondary"
          loading={run.isPending}
          disabled={run.isPending}
          onClick={() => void run.mutateAsync()}
        >
          Investigate
        </Button>
      </div>
      {suggestions.map((suggestion) => (
        <article key={suggestion.id} className="assistant-card">
          <p className="assistant-card__title">{suggestion.title}</p>
          <p className="assistant-card__body">{suggestion.body}</p>
          <div className="assistant-card__actions">
            <Button
              size="sm"
              emphasis="ghost"
              onClick={() => void dismiss.mutateAsync(suggestion.id)}
            >
              Not now
            </Button>
            <Button size="sm" emphasis="primary" onClick={() => void confirm(suggestion)}>
              Make rule
            </Button>
          </div>
        </article>
      ))}
      {run.isError && (
        <p className="assistant-card__error">Couldn&apos;t investigate just now. Try again.</p>
      )}
    </div>
  );
}
